/**
 * Edit Customer Name Module
 * Reusable module for editing customer names anywhere in Shopify
 * 
 * Usage:
 * 1. Include the CSS: <link rel="stylesheet" href="edit-customer-name.css">
 * 2. Include this script: <script src="edit-customer-name.js"></script>
 * 3. Initialize: EditCustomerName.init() - Uses window.API_BASE_URL automatically
 *    OR: EditCustomerName.init({ middlewareUrl: '...', shopDomain: '...' }) to override
 * 4. Call from button: <button class="edit-name-btn" data-first-name="John" data-last-name="Doe">Edit</button>
 *    OR: EditCustomerName.open('John', 'Doe')
 * 
 * Requirements:
 * - window.API_BASE_URL should be set (e.g., 'https://our-middleware.com/api')
 * - window.customer_id, window.customer_email, window.token_hmac should be set
 * - window.shop_domain (optional, defaults to 'alyve-store.myshopify.com')
 */

(function() {
  'use strict';

  const EditCustomerName = {
    // Configuration
    config: {
      middlewareUrl: window.API_BASE_URL ? window.API_BASE_URL + '/proxy_update_fulfilment_bottle_name' : '', // window.API_BASE_URL or set default
      currentFirstName: '',
      currentLastName: '',
      shopDomain: window.shop_domain || 'alyve-store.myshopify.com' // Get from window or use default
    },

    // DOM elements 
    modal: null,
    form: null,
    firstNameInput: null,
    lastNameInput: null,
    updateBtn: null,

    /**
     * Initialize the module (optional - will use defaults from window variables)
     * @param {Object} options - Optional configuration to override defaults
     * @param {string} options.middlewareUrl - Override middleware URL (defaults to window.API_BASE_URL + '/proxy_update_customer_name')
     * @param {string} options.shopDomain - Override shop domain (defaults to window.shop_domain or 'alyve-store.myshopify.com')
     */
    init: function(options) {
      options = options || {};
      
      // Use provided middlewareUrl or get from window.API_BASE_URL or use default
      this.config.middlewareUrl = options.middlewareUrl || 
        (window.API_BASE_URL ? window.API_BASE_URL + '/proxy_update_fulfilment_bottle_name' : '');
      
      // Use provided shopDomain or get from window or use default
      this.config.shopDomain = options.shopDomain || 
        window.shop_domain || 
        'alyve-store.myshopify.com';
      
      // Validate middlewareUrl
      if (!this.config.middlewareUrl) {
        console.error('EditCustomerName: middlewareUrl not found. Set window.API_BASE_URL or pass middlewareUrl in init()');
        return;
      }

      // Create modal HTML
      this.createModal();

      // Set up event listeners
      this.setupEventListeners();

      // Auto-bind to buttons with class 'edit-name-btn'
      this.autoBindButtons();

      console.log('EditCustomerName module initialized');
    },

    /**
     * Create modal HTML structure
     */
    createModal: function() {
      // Check if modal already exists
      if (document.getElementById('editCustomerNameModal')) {
        return;
      }

      const modalHTML = `
  <div class="edit-name-modal-overlay" id="editCustomerNameModal">
    <div class="edit-name-modal">
      <button class="edit-name-modal-close" id="editNameCloseBtn" aria-label="Close modal">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" 
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
      
      <h2 class="edit-name-modal-heading">Edit your name</h2>
      
      <form id="editCustomerNameForm">
        <div class="edit-name-form-group">
          <label for="editCustomerFirstName">First Name</label>
          <input 
            type="text" 
            id="editCustomerFirstName" 
            name="firstName" 
            maxlength="15"
          >
        </div>
        
        <div class="edit-name-form-group" style="display: none;">
          <label for="editCustomerLastName">Last Name</label>
          <input 
            type="text" 
            id="editCustomerLastName" 
            name="lastName" 
            maxlength="15"
          >
        </div>
        
        <div class="edit-name-message" id="editNameMessage"></div>
        
        <button 
          type="submit" 
          class="edit-name-update-btn" 
          id="editNameUpdateBtn" 
          disabled
        >
          Update
        </button>
      </form>
    </div>
  </div>
`;

      // Append to body
      document.body.insertAdjacentHTML('beforeend', modalHTML);

      // Cache DOM elements
      this.modal = document.getElementById('editCustomerNameModal');
      this.form = document.getElementById('editCustomerNameForm');
      this.firstNameInput = document.getElementById('editCustomerFirstName');
      this.lastNameInput = document.getElementById('editCustomerLastName');
      this.updateBtn = document.getElementById('editNameUpdateBtn');
    },

    /**
     * Set up event listeners
     */
    setupEventListeners: function() {
      if (!this.modal) return;

      const closeBtn = document.getElementById('editNameCloseBtn');
      
      // Close button
      closeBtn.addEventListener('click', () => this.close());

      // Close on overlay click
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.close();
        }
      });

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('active')) {
          this.close();
        }
      });

      // Form validation
      this.firstNameInput.addEventListener('input', () => this.validateForm());
      this.lastNameInput.addEventListener('input', () => this.validateForm());

      // Form submission
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submitForm();
      });
    },

    /**
     * Auto-bind to buttons with class 'edit-name-btn'
     */
    autoBindButtons: function() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.edit-name-btn');
        if (btn) {
          e.preventDefault();
          const firstName = btn.dataset.firstName || this.config.currentFirstName;
          const lastName = btn.dataset.lastName || this.config.currentLastName;
          this.open(firstName, lastName);
        }
      });
    },

    /**
     * Open the modal
     * @param {string} firstName - First name (optional)
     * @param {string} lastName - Last name (optional)
     */
    open: function(firstName, lastName) {
      if (!this.modal) {
        console.error('EditCustomerName: Modal not initialized. Call init() first.');
        return;
      }

      // Use provided values or fall back to config
      const first = firstName || this.config.currentFirstName || '';
      const last = lastName || this.config.currentLastName || '';

      // Set input values
      this.firstNameInput.value = first;
      this.lastNameInput.value = last;

      // Show modal
      this.modal.classList.add('active');
      this.firstNameInput.focus();

      // Validate form
      this.validateForm();

      // Clear any previous messages
      this.showMessage('', '');
    },

    /**
     * Close the modal
     */
    close: function() {
      if (!this.modal) return;

      this.modal.classList.remove('active');
      
      // Reset form
      this.firstNameInput.value = '';
      this.lastNameInput.value = '';
      this.updateBtn.disabled = true;
      this.showMessage('', '');
    },

    /**
     * Validate form and enable/disable update button
     */
    validateForm: function() {
      const firstName = this.firstNameInput.value.trim();
      const lastName = this.lastNameInput.value.trim();
      const maxLength = 15;

      // Check if names are too long
      if (firstName.length > maxLength || lastName.length > maxLength) {
        this.updateBtn.disabled = true;
        this.showMessage('Name is too long', 'error');
        return;
      }

      // Check if both fields are filled
      if (firstName && lastName) {
        this.updateBtn.disabled = false;
        this.showMessage('', ''); // Clear error message if valid
      } else {
        this.updateBtn.disabled = true;
        // Only clear message if both fields are empty (not showing "too long" error)
        if (!firstName && !lastName) {
          this.showMessage('', '');
        }
      }
    },

    /**
     * Show message to user
     * @param {string} message - Message text
     * @param {string} type - 'success' or 'error'
     */
    showMessage: function(message, type) {
      const messageEl = document.getElementById('editNameMessage');
      if (!messageEl) return;

      messageEl.textContent = message;
      messageEl.className = 'edit-name-message';
      
      if (type === 'success') {
        messageEl.classList.add('success');
      } else if (type === 'error') {
        messageEl.classList.add('error');
      }
    },


    /**
     * Submit form to middleware
     */
    submitForm: function() {
      const firstName = this.firstNameInput.value.trim();
      const lastName = this.lastNameInput.value.trim();
      const maxLength = 15;

      if (!firstName || !lastName) {
        this.showMessage('Please fill in both first and last name', 'error');
        return;
      }

      // Validate length before submitting
      if (firstName.length > maxLength || lastName.length > maxLength) {
        this.showMessage('Name is too long', 'error');
        this.updateBtn.disabled = true;
        return;
      }

      // Disable button during submission
      this.updateBtn.disabled = true;
      this.updateBtn.textContent = 'Updating...';
      this.showMessage('', '');

      // Get customer data from window (set by your Shopify theme)
      const customerId = window.customer_id;
      const customerEmail = window.customer_email;
      const tokenHmac = window.token_hmac;

      if (!customerId) {
        this.showMessage('Customer ID not found. Please ensure you are logged in.', 'error');
        this.updateBtn.disabled = false;
        this.updateBtn.textContent = 'Update';
        return;
      }

      // Prepare headers matching your middleware pattern
      const headers = {
        'Content-Type': 'application/json',
        'x-customer-email': customerEmail || '',
        'x-customer-id': customerId,
        'x-customer-signature': tokenHmac || '',
        'x-shopify-shop-domain': this.config.shopDomain
      };

      // Make API call
      fetch(this.config.middlewareUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          firstName: firstName,
          lastName: lastName
        })
      })
      .then(response => {
        if (!response.ok) {
          return response.json().then(err => {
            throw new Error(err.error || err.message || 'Failed to update name');
          });
        }
        return response.json();
      })
      .then(data => {
        if (data.success) {
          this.showMessage('Name updated successfully!', 'success');
          
          // Update config with new values
          this.config.currentFirstName = firstName;
          this.config.currentLastName = lastName;

          // Trigger custom event for other scripts to listen to
          const event = new CustomEvent('customerNameUpdated', {
            detail: {
              firstName: firstName,
              lastName: lastName
            }
          });
          document.dispatchEvent(event);
          console.log(firstName , "Update Name")
          // Close modal after 1.5 seconds
          setTimeout(() => {
            this.close();
            // reload page or update UI
            if (window.location.reload) {
              // Uncomment to reload page after update if required 
              // window.location.reload();
            }
          }, 1500);
        } else {
          throw new Error(data.error || 'Failed to update name');
        }
      })
      .catch(error => {
        console.error('EditCustomerName error:', error);
        this.showMessage(error.message || 'An error occurred. Please try again.', 'error');
        this.updateBtn.disabled = false;
        this.updateBtn.textContent = 'Update';
      });
    }
  };

  // Export to global scope
  window.EditCustomerName = EditCustomerName;

})();

